import { ReactNode, useEffect, useRef, useState } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Tokens } from '@okta/okta-auth-js';
import { routes } from '../../utils/constants/routes';
import { errorsMap } from '../../utils/constants/api/errorsMap';
import { showToast } from '../../components/Toast/showToast';
import useSamlTestingConnection from './hooks/useSamlTestingConnection';
import { SmOktaAuth } from '../../utils/oktaInstance';
import { useSetOktaAuthorizationHeaders } from './hooks/useSetOktaAuthorizationHeaders';
import { useSetCsrfToken } from './hooks/useSetCsrfToken';
import { Loader } from '../Routes.style';
import { sessionExpired } from '../../store/auth/auth.action';

const ParseTokens = ({ children }: { children: ReactNode }) => {
  const isMounted = useRef(false);
  const { addToken, setExistingTokens } = useSetOktaAuthorizationHeaders();
  const history = useHistory();
  const dispatch = useDispatch();
  const { oktaAuth } = useOktaAuth();
  const { tokenManager, token } = oktaAuth;
  const { flagSamlTestConnectionEnd } = useSamlTestingConnection();
  const [displayLoader, setDisplayLoader] = useState(true);
  const { setCsrToken } = useSetCsrfToken();

  const showLoader = (flag = true) => {
    isMounted.current && setDisplayLoader(flag);
  };
  const hideLoader = (flag = false) => {
    isMounted.current && setDisplayLoader(flag);
  };

  const handleSamlTestStep = async (samlTestStep: string, loggedIdp: unknown) => {
    const { idp, samlTestId } = JSON.parse(samlTestStep);

    if (samlTestId) {
      await setCsrToken();
    }

    const testStatus = loggedIdp === idp ? 'success' : 'failed';
    await flagSamlTestConnectionEnd({ testStatus });
    hideLoader();
  };

  const handleNormalTokenFlow = async (tokens: Tokens, callCsrfFlag = true) => {
    tokenManager.setTokens(tokens);
    addToken(tokens.accessToken, tokens.idToken);
    await setCsrToken(callCsrfFlag);
    hideLoader();
  };

  const parseOktaTokensFromUrl = async () => {
    const samlTestStep = window.sessionStorage.getItem('samlTestStep');

    try {
      const res = await token.parseFromUrl();
      const loggedIdp = res && res.tokens.idToken.claims.idp;

      if (samlTestStep) {
        await handleSamlTestStep(samlTestStep, loggedIdp);
      } else {
        await handleNormalTokenFlow(res.tokens);
      }
    } catch (e) {
      // this console is used in order to facilitate debugging in a case of error from idp
      console.log({ ...e }, 'social login error'); // eslint-disable-line

      if (e?.errorCode?.includes('access_denied') && e?.errorSummary?.includes('email')) {
        // the case of mail not public on a social idp (eg. github)
        // dispatch sessionExpired to set loginStatus to 'resolved' so Routes.tsx can render the error page
        dispatch(sessionExpired());
        history.push(routes.missingEmailSocialLogin);
        hideLoader();
      } else {
        // any other okta related error
        showToast(e?.errorSummary || errorsMap.error_general.message);

        if (samlTestStep) {
          await flagSamlTestConnectionEnd({ testStatus: 'failed' }); // failed saml test
        }

        hideLoader();
      }
    }
  };

  const getExistingTokens = async () => {
    const { accessToken, idToken } = await tokenManager.getTokens();

    setExistingTokens(accessToken, idToken);
    hideLoader();
  };

  const setToken = async () => {
    if (SmOktaAuth.getInstance().isLoginRedirect()) {
      showLoader();
      await parseOktaTokensFromUrl();
    } else {
      await setCsrToken();
      await getExistingTokens();
    }
  };

  useEffect(() => {
    isMounted.current = true;
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    setToken();

    return () => {
      isMounted.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (displayLoader) {
    return <Loader dataTestId="loader-parse-tokens-on-redirect" />;
  }

  return <>{children}</>;
};

export default ParseTokens;
